import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { API_URL } from 'env';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SettingData {
    /** Thông tin công ty */
    company_name?: string;
    address?: string;
    tax_number?: string;
    legal_representative?: string;
    issue_date?: string;
    operation_date?: string;
    contact_phone?: string;
    is_active?: string;
    contact_email?: string;
    hotline?: string;
    business_hours?: string;

    /** Logo */
    logo?: string;

    /** Menu & ChatGPT config */
    menu_header?: unknown[];
    language_chatgpt_content?: string[];
    creativity_chatgpt_content?: string[];

    /** Cho phép truy cập bất kỳ key nào khác trong JSON */
    [key: string]: unknown;
}

export interface SettingContextType {
    settings: SettingData;
    isLoading: boolean;
    /** Gọi lại API setting (bỏ cache) */
    refreshSettings: () => Promise<void>;
}

// ─── Cache helpers ───────────────────────────────────────────────────────────

const CACHE_KEY = 'app_setting_cache';
const CACHE_LOGO_KEY = 'app_setting_logo';
const CACHE_TTL = 5 * 60 * 1000; // 5 phút

interface CachedSetting {
    data: SettingData;
    timestamp: number;
}

function getCachedSettings(): SettingData | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const cached: CachedSetting = JSON.parse(raw);
        if (Date.now() - cached.timestamp > CACHE_TTL) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }
        return cached.data;
    } catch {
        localStorage.removeItem(CACHE_KEY);
        return null;
    }
}

function setCachedSettings(data: SettingData): void {
    try {
        const payload: CachedSetting = { data, timestamp: Date.now() };
        localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
        // Lưu riêng logo để dùng làm fallback tức thì
        if (data.logo) {
            localStorage.setItem(CACHE_LOGO_KEY, data.logo);
        }
    } catch {
        // quota exceeded – bỏ qua
    }
}

function getCachedLogo(): string | undefined {
    try {
        return localStorage.getItem(CACHE_LOGO_KEY) || undefined;
    } catch {
        return undefined;
    }
}

// ─── Context ─────────────────────────────────────────────────────────────────

const SettingContext = createContext<SettingContextType | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export const SettingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<SettingData>(() => {
        // Dùng logo đã cache lần gần nhất làm fallback ngay khi render
        const cachedLogo = getCachedLogo();
        return cachedLogo ? { logo: cachedLogo } : {};
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const fetchSettings = useCallback(async (useCache = true) => {
        // 1. Thử lấy từ cache trước
        if (useCache) {
            const cached = getCachedSettings();
            if (cached) {
                setSettings(cached);
                setIsLoading(false);
                return;
            }
        }

        // 2. Gọi API
        setIsLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/v1/fe/other/setting?language=vi&is_code=1&code=setting`,
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const result = await response.json();

            if (result?.status && result.data?.json) {
                const parsed: SettingData = JSON.parse(result.data.json);
                setSettings(parsed);
                setCachedSettings(parsed);
            }
        } catch (error) {
            console.error('Failed to fetch app settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const refreshSettings = useCallback(async () => {
        localStorage.removeItem(CACHE_KEY);
        await fetchSettings(false);
    }, [fetchSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const value: SettingContextType = { settings, isLoading, refreshSettings };

    return (
        <SettingContext.Provider value={value}>
            {children}
        </SettingContext.Provider>
    );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useSetting = (): SettingContextType => {
    const context = useContext(SettingContext);
    if (!context) {
        throw new Error('useSetting must be used within a SettingProvider');
    }
    return context;
};
